import { ContentfulConstraint, ContentfulQuery, mergeConstraints, removeFalsy } from 'services/ContentfulService/utils'
import { CONTENT_TYPE } from 'generated/types/contentful'
import useSWR from 'swr'
import Logger from 'services/Logger'
import { Entry } from 'contentful'

export type FetcherType = [ContentfulConstraint[], number]
type ContentfulHookResponse<Type> = [Type | undefined, boolean, Error | undefined]
type SourceParser<Type, SourceType> = (raw: Entry<SourceType>) => Type | undefined
type Fetcher<Type> = (constraints: string, count: number) => Promise<Array<Type>>
type HookType<Type> = (props: ContentfulHookProps, initialData?: Type[]) => ContentfulHookResponse<Array<Type>>
type SSRFetchType<Type> = (props: ContentfulHookProps) => Promise<Array<Type>>

type HookOutput<Type> = [HookType<Type>, SSRFetchType<Type>, Fetcher<Type>]

export const buildContentfulHooks = <Type, SourceType>(
	contentType: CONTENT_TYPE,
	parseSource: SourceParser<Type, SourceType>
): HookOutput<Type> => {
	const contentfulFetcher = async (constraints: string, count = 100, offset = 0): Promise<Array<Type>> => {
		try {
			const parsed = JSON.parse(constraints)
			const constraintsObj = constraints ? mergeConstraints(parsed) : {}
			const query: ContentfulQuery = {
				order: '-sys.createdAt',
				limit: count,
				content_type: contentType,
				skip: offset,
				include: 6,
				...constraintsObj
			}
			const { fetchEntries } = await import('services/ContentfulService/client')
			const collection = await fetchEntries<SourceType>(query)
			return removeFalsy(collection.items.map(item => parseSource(item)))
		} catch (err) {
			Logger.error(`Error fetching contentful resource ${contentType}`, err)
			throw err
		}
	}

	const useAsSSR = async ({ constraints, count = 100 }: ContentfulHookProps): Promise<Array<Type>> => {
		return contentfulFetcher(JSON.stringify(constraints), count)
	}

	const useAsHook = (
		{ constraints, count = 100, offset = 0 }: ContentfulHookProps,
		initialData?: Type[]
	): ContentfulHookResponse<Array<Type>> => {
		const { data, error } = useSWR<Type[]>([JSON.stringify(constraints), count, offset], contentfulFetcher, {
			initialData
		})
		return [data, !data && !error, error]
	}

	return [useAsHook, useAsSSR, contentfulFetcher]
}

export interface ContentfulHookProps {
	constraints: ContentfulConstraint[]
	count?: number
	offset?: number
	include?: number
}
